import {type FocusEvent, useLayoutEffect, useRef, useState, useSyncExternalStore} from 'react';

import {isNil, isNotNil} from 'ramda';

import {VehicleMotWarningResponseBody} from '@dms/api/core';

import {useAdaptiveElementSize} from '../../../hooks/useAdaptiveElementSize';
import {DashboardAdaptiveDensityController} from '../../../types/DashboardAdaptiveDensityController';
import {DashboardTileSize} from '../../../types/DashboardTileSize';
import {
  getVehicleWarehouseMotLayout,
  type VehicleWarehouseMotLayout,
} from '../../../utils/getVehicleWarehouseMotLayout';
import {toDashboardAdaptiveCssHeight} from '../../../utils/toDashboardAdaptiveCssHeight';
import {EMPTY_SECTION_HEIGHTS} from '../constants/emptySectionHeights';
import {FocusedVehicleMotContent} from '../types/FocusedVehicleMotContent';
import {FocusReleaseMeasurement} from '../types/FocusReleaseMeasurement';
import {FocusRetention} from '../types/FocusRetention';
import {VehicleMotMeasurement} from '../types/VehicleMotMeasurement';
import {VehicleMotSectionMeasurements} from '../types/VehicleMotSectionMeasurements';
import {getVehicleMotWarningLayoutKey} from '../utils/getVehicleMotWarningLayoutKey';
import {wouldRemoveFocusedVehicleMotContent} from '../utils/wouldRemoveFocusedVehicleMotContent';

const HEIGHT_TOLERANCE = 0.5;
const NOT_DRAGGING = () => false;

interface UseVehicleWarehouseMotLayoutOptions {
  size: DashboardTileSize;
  expiredRows: VehicleMotWarningResponseBody[];
  expiringRows: VehicleMotWarningResponseBody[];
  isDetailReady: boolean;
  baselineLayout: VehicleWarehouseMotLayout;
  coordinator: DashboardAdaptiveDensityController;
}

// eslint-disable-next-line complexity, max-statements -- Pre-existing violation
export function useVehicleWarehouseMotLayout(options: UseVehicleWarehouseMotLayoutOptions) {
  const isDragging = useSyncExternalStore(
    options.coordinator.subscribeDragging,
    options.coordinator.getIsDragging,
    NOT_DRAGGING
  );
  const rowLayoutKey = JSON.stringify([
    options.expiredRows.map(getVehicleMotWarningLayoutKey),
    options.expiringRows.map(getVehicleMotWarningLayoutKey),
  ]);
  const [regionMeasurementRef, availableWidth, availableHeight] =
    useAdaptiveElementSize<HTMLDivElement>({
      resetKey: options.size,
    });
  const regionElementRef = useRef<HTMLDivElement>(null);
  const [aggregateMeasurement, setAggregateMeasurement] = useState<VehicleMotMeasurement>({
    contentKey: rowLayoutKey,
    width: null,
    value: null,
  });
  const [stackedGapMeasurement, setStackedGapMeasurement] = useState<VehicleMotMeasurement>({
    contentKey: rowLayoutKey,
    width: null,
    value: null,
  });
  const [expiredMeasurements, setExpiredMeasurements] = useState<VehicleMotSectionMeasurements>({
    contentKey: rowLayoutKey,
    width: null,
    values: [...EMPTY_SECTION_HEIGHTS],
  });
  const [expiringMeasurements, setExpiringMeasurements] = useState<VehicleMotSectionMeasurements>({
    contentKey: rowLayoutKey,
    width: null,
    values: [...EMPTY_SECTION_HEIGHTS],
  });
  const [settledLayout, setSettledLayout] = useState<{
    key: string;
    value: VehicleWarehouseMotLayout;
  } | null>(null);
  const [focusedContent, setFocusedContent] = useState<FocusedVehicleMotContent | null>(null);
  const [focusRetention, setFocusRetention] = useState<FocusRetention | null>(null);
  const [focusReleaseMeasurement, setFocusReleaseMeasurement] =
    useState<FocusReleaseMeasurement | null>(null);

  const isCurrentGeneration = (contentKey: string) => contentKey === rowLayoutKey;
  const aggregateHeight = isCurrentGeneration(aggregateMeasurement.contentKey)
    ? aggregateMeasurement.value
    : null;
  const stackedGap = isCurrentGeneration(stackedGapMeasurement.contentKey)
    ? stackedGapMeasurement.value
    : null;
  const expiredSectionHeights = isCurrentGeneration(expiredMeasurements.contentKey)
    ? expiredMeasurements.values
    : EMPTY_SECTION_HEIGHTS;
  const expiringSectionHeights = isCurrentGeneration(expiringMeasurements.contentKey)
    ? expiringMeasurements.values
    : EMPTY_SECTION_HEIGHTS;
  const aggregateWidth = isCurrentGeneration(aggregateMeasurement.contentKey)
    ? aggregateMeasurement.width
    : null;
  const stackedGapWidth = isCurrentGeneration(stackedGapMeasurement.contentKey)
    ? stackedGapMeasurement.width
    : null;
  const expiredSectionWidth = isCurrentGeneration(expiredMeasurements.contentKey)
    ? expiredMeasurements.width
    : null;
  const expiringSectionWidth = isCurrentGeneration(expiringMeasurements.contentKey)
    ? expiringMeasurements.width
    : null;
  const measurementsReady =
    aggregateHeight !== null &&
    stackedGap !== null &&
    availableWidth !== null &&
    aggregateWidth !== null &&
    stackedGapWidth !== null &&
    expiredSectionWidth !== null &&
    expiringSectionWidth !== null &&
    Math.abs(aggregateWidth - availableWidth) <= HEIGHT_TOLERANCE &&
    Math.abs(stackedGapWidth - availableWidth) <= HEIGHT_TOLERANCE &&
    Math.abs(expiredSectionWidth - expiringSectionWidth) <= HEIGHT_TOLERANCE &&
    expiredSectionHeights.every(isNotNil) &&
    expiringSectionHeights.every(isNotNil);
  const layoutAvailableHeight =
    focusRetention?.availableHeight ?? focusReleaseMeasurement?.height ?? availableHeight;
  const measuredLayout = measurementsReady
    ? getVehicleWarehouseMotLayout({
        size: options.size,
        availableHeight: layoutAvailableHeight,
        aggregateHeight,
        stackedGap,
        expiredSectionHeights,
        expiringSectionHeights,
        expiredRowCount: options.expiredRows.length,
        expiringRowCount: options.expiringRows.length,
        canShowDetail: options.isDetailReady,
      })
    : null;
  const measuredLayoutKey = isNotNil(measuredLayout)
    ? JSON.stringify([rowLayoutKey, measuredLayout])
    : null;
  const shouldStartFocusRetention =
    !isDragging &&
    isNil(focusRetention) &&
    isNil(focusReleaseMeasurement) &&
    isNotNil(settledLayout) &&
    isNotNil(measuredLayout) &&
    isNotNil(availableHeight) &&
    wouldRemoveFocusedVehicleMotContent(focusedContent, measuredLayout);
  const currentFocusRetention = shouldStartFocusRetention
    ? {availableHeight, layout: settledLayout.value}
    : focusRetention;

  if (currentFocusRetention !== focusRetention) {
    setFocusRetention(currentFocusRetention);
  }

  const currentSettledLayout =
    !isDragging &&
    isNil(currentFocusRetention) &&
    isNotNil(measuredLayoutKey) &&
    isNotNil(measuredLayout)
      ? {key: measuredLayoutKey, value: measuredLayout}
      : settledLayout;

  if (
    isNotNil(currentSettledLayout) &&
    (isNil(settledLayout) || currentSettledLayout.key !== settledLayout.key)
  ) {
    setSettledLayout(currentSettledLayout);
  }

  const layout =
    currentFocusRetention?.layout ??
    (isDragging ? settledLayout?.value : measuredLayout) ??
    settledLayout?.value ??
    options.baselineLayout;
  const hasInitialMeasurements = isNotNil(currentSettledLayout);

  // Reads the region's committed height before paint, then drops the pinned height a frame later,
  // so releasing focus never paints the tile at an unmeasured size.
  useLayoutEffect(
    function handOffVehicleWarehouseAfterFocusRelease() {
      if (isNil(focusReleaseMeasurement)) {
        return;
      }

      if (focusReleaseMeasurement.needsMeasurement) {
        const height = regionElementRef.current?.getBoundingClientRect().height;
        if (isNotNil(height)) {
          setFocusReleaseMeasurement({height, needsMeasurement: false});
        }
        return;
      }

      const animationFrame = window.requestAnimationFrame(() => {
        setFocusReleaseMeasurement((current) =>
          current?.needsMeasurement === false ? null : current
        );
      });

      return () => window.cancelAnimationFrame(animationFrame);
    },
    [focusReleaseMeasurement]
  );

  const minimumHeightValue =
    currentFocusRetention?.layout.contentHeight ?? layout.minimumHeight ?? 0;
  const minimumHeight = toDashboardAdaptiveCssHeight(minimumHeightValue);

  const releaseFocusedLayout = () => {
    if (isNil(currentFocusRetention)) {
      return;
    }

    setFocusReleaseMeasurement({
      height: currentFocusRetention.availableHeight,
      needsMeasurement: true,
    });
    setFocusRetention(null);
  };

  const onFocusCapture = (event: FocusEvent<HTMLDivElement>) => {
    const focusedElement = event.target;
    if (!(focusedElement instanceof HTMLElement)) {
      return;
    }

    const visibleContent = focusedElement.closest<HTMLElement>('[data-vehicle-warehouse-mot-mode]');
    const focusedRow = focusedElement.closest<HTMLElement>('[data-vehicle-warehouse-visible-row]');
    const mode = visibleContent?.dataset.vehicleWarehouseMotMode;
    if (mode !== 'aggregate' && mode !== 'detail') {
      return;
    }

    setFocusedContent({
      mode,
      category: focusedRow?.dataset.vehicleWarehouseVisibleRow,
      rowIndex: isNotNil(focusedRow)
        ? Number(focusedRow.dataset.vehicleWarehouseVisibleRowIndex)
        : undefined,
    });
  };

  const onBlurCapture = (event: FocusEvent<HTMLDivElement>) => {
    const nextFocusedElement = event.relatedTarget;
    if (nextFocusedElement instanceof Node && event.currentTarget.contains(nextFocusedElement)) {
      return;
    }

    setFocusedContent(null);
    releaseFocusedLayout();
  };

  return {
    regionMeasurementRef,
    regionElementRef,
    rowLayoutKey,
    layout,
    hasInitialMeasurements,
    minimumHeight,
    onFocusCapture,
    onBlurCapture,
    setters: {
      setAggregateMeasurement,
      setStackedGapMeasurement,
      setExpiredMeasurements,
      setExpiringMeasurements,
    },
  };
}
